import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { getProviderCallRateDetail } from '../queries/call-cost-detail.graphql';
import {
   GetProviderCallRateDetail,
   GetProviderCallRateDetailVariables,
} from '../queries/generated/GetProviderCallRateDetail';
import { CallCostDetailApiService } from '../services/call-cost-detail-api.service';
import * as CallCostDetailActions from './call-cost-detail.actions';
@Injectable()
export class CallCostDetailEffects {
   loadScheduleDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            CallCostDetailActions.callCostDetailLoad,
            CallCostDetailActions.saveCallCostDetailSuccess,
            CallCostDetailActions.callCostDetailCancel,
            CallCostDetailActions.updateCallCostId
         ),
         withLatestFrom(this.twoColumnLayoutService.selectedId$, this.twoColumnLayoutService.firstItemId$),
         switchMap(([action, selectedId, firstItemId]) => {
            if (selectedId === null) {
               return of(CallCostDetailActions.loadPageNoCallCost());
            }
            this.twoColumnLayoutService.setShouldShowDetailView(firstItemId !== null);

            const providerCallRateId = selectedId;

            return this.apiService
               .query<GetProviderCallRateDetail, GetProviderCallRateDetailVariables>({
                  query: getProviderCallRateDetail,
                  variables: { id: providerCallRateId },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute)),
                     this.store.pipe(select(getSelectedAccountId))
                  ),
                  map(([callCostResult, privileges, route, accountId]) => {
                     if (!hasSelectedPrivilege(Privileges.ManageProviderCallRates_View, accountId ?? 0, privileges)) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return CallCostDetailActions.callCostDetailAccessDenied();
                     }
                     return CallCostDetailActions.loadCallCostDetailSuccess({
                        data: callCostResult.data.providerCallRate,
                     });
                  }),
                  catchError((error) => {
                     return of(CallCostDetailActions.loadCallCostDetailFailure(error));
                  })
               );
         })
      );
   });

   deleteCallCost = createEffect(() => {
      return this.actions$.pipe(
         ofType(CallCostDetailActions.deleteCallCost),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this Call Cost?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Call Cost?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.callCostDetailApiService.deleteCallCost(action.providerCallRateId).pipe(
                        map(() => {
                           return CallCostDetailActions.deleteCallCostSuccess(action);
                        }),
                        catchError((error) => {
                           return of(CallCostDetailActions.deleteCallCostFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteCallCostSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(CallCostDetailActions.deleteCallCostSuccess),
            withLatestFrom(this.store.pipe(select(selectRouteParams))),
            mergeMap(([action]) => {
               this.twoColumnLayoutService.itemDeleted();
               this.snackBar.open(`Call Cost ${action.destination} deleted`);

               return [CallCostDetailActions.callCostDetailLoad()];
            })
         );
      },
      { dispatch: true }
   );

   saveCallCostDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(CallCostDetailActions.saveCallCostDetailSuccess),
            tap(() => {
               this.twoColumnLayoutService.notifyItemAddedOrDeleted();
               this.snackBar.open('Call Cost Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private callCostDetailApiService: CallCostDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private dialogService: LcsDialogService,
      private location: Location,
      private twoColumnLayoutService: TwoColumnLayoutService
   ) {}
}
